import * as log from '@sb/log';
import type {
  CBFViolation,
  ListViolatedCBFsResponse,
} from '@sb/messenger/auto_generated/messenger/compliance_control_bot_service';
import type {
  ComplianceControlBotConnector,
  FeathersBotConnector,
} from '@sb/messenger/connectors/generated';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { CBFAxis, CBFSign } from './cbfLabels';
import {
  EXTERNAL_COLLISION_LABEL,
  FORCE_LIMIT_LABEL,
  SELF_COLLISION_LABEL,
  SINGULARITY_LABEL,
  eeBoundaryLabel,
  jointLimitLabel,
  wholeBodyBoundaryLabel,
} from './cbfLabels';

/**
 * Maps a raw CBF barrier name (as produced by the compliance controller's
 * `_barrier_segments`, optionally `stageN:`-prefixed) to the operator-
 * facing label. Uses the shared label constructors in `./cbfLabels` so the
 * wording stays in lockstep with the in-session teleop toasts. Falls back to
 * the de-prefixed raw name for anything unrecognized.
 */
export function labelForBarrier(rawName: string): string {
  const name = rawName.replace(/^stage\d+:/, '');

  const ee = name.match(/^ee_set_containment_(max|min)_([xyz])$/);

  if (ee) {
    const sign: CBFSign = ee[1] === 'max' ? '+' : '−';

    return eeBoundaryLabel(sign, ee[2].toUpperCase() as CBFAxis);
  }

  const joint = name.match(/^joint_limit_(max|min)_(\d+)$/);

  if (joint) {
    return jointLimitLabel(
      Number(joint[2]),
      joint[1] === 'max' ? 'upper' : 'lower',
    );
  }

  if (name === 'singularity_avoidance') return SINGULARITY_LABEL;

  const wb = name.match(/^whole_body_(upper|lower)_([xyz])_\d+$/);

  if (wb) {
    const sign: CBFSign = wb[1] === 'upper' ? '+' : '−';

    return wholeBodyBoundaryLabel(sign, wb[2].toUpperCase() as CBFAxis);
  }

  if (name.startsWith('collision_avoidance_')) return EXTERNAL_COLLISION_LABEL;
  if (name.startsWith('self_collision_')) return SELF_COLLISION_LABEL;
  if (name.startsWith('force_limit_')) return FORCE_LIMIT_LABEL;

  return name;
}

/** De-duplicated, operator-facing labels for a set of violated CBFs. */
export function formatViolations(violations: CBFViolation[]): string {
  const labels: string[] = [];

  violations.forEach((v) => {
    const label = labelForBarrier(v.name);

    if (!labels.includes(label)) labels.push(label);
  });

  return labels.join(', ');
}

export type CBFCheckResult =
  | { ok: true }
  | {
      ok: false;
      /** Shown to the operator, appended after "Cannot start teleop: ". */
      operatorFacingReason: string;
      violations: CBFViolation[];
      /** Which service or call actually failed. Logged, never shown. */
      internalReason?: string;
    };

const ns = log.namespace('complianceCBF');

/** Logs the internal reason so no call site has to. */
function failed(
  operatorFacingReason: string,
  internalReason: string,
  violations: CBFViolation[] = [],
): CBFCheckResult {
  log.warn(ns`check`, 'Compliance check failed', {
    operatorFacingReason,
    internalReason,
  });

  return { ok: false, operatorFacingReason, violations, internalReason };
}

// Operator-facing. Name only the bounds, never a service or feature. Offer
// "try again" only where waiting clears it, which is why UNAVAILABLE does not.
const NOT_READY_REASON = 'bounds are not ready yet, try again in a few moments';

const UNAVAILABLE_REASON = 'bounds cannot be checked right now';

/**
 * A just-saved change hasn't reached the running controller yet; transient.
 */
const STALE_CONFIG_REASON = 'bounds are updating, try again in a moment';

/** feathers-bot is on-box and answers from an in-memory cache; keep it short. */
const GET_COMPLIANCE_CONFIG_TIMEOUT_MS = 5_000;

// Generous ceiling for the one-shot query: the controller's first h(x) call may
// trace/compile on control-box CPU. Well above expected latency, but bounded so
// a wedged/absent controller fails closed promptly rather than inheriting the
// messenger default (5s) and, worse, escaping as an unhandled rejection (which
// would surface as a hidden Internal routine failure).
const LIST_VIOLATED_CBFS_TIMEOUT_MS = 15_000;

/**
 * The single teleop-start compliance gate, shared by every entry point (the
 * raw `/teleop/start` and `startTeleopSession` controllers in
 * `libs/sb-robot-api`, and the Teleop routine step). Owns the whole policy so
 * call sites collapse to one branch:
 *   - `complianceMode` flag off        -> `{ ok: true }` (nothing to check)
 *   - connector missing                -> not-connected reason (fail closed)
 *   - RPC error/timeout               -> no-response reason (fail closed)
 *   - controller reports not available -> not-ready reason (fail closed)
 *   - any enabled CBF violated         -> "in violation of <labels>"
 *   - running config != latest saved   -> stale-config reason (fail closed)
 *   - otherwise                        -> `{ ok: true }`
 *
 * The last case is the config-version handshake: the controller can report
 * "within bounds" while still running an OLD config, because a just-saved
 * change reaches it asynchronously and then takes seconds to re-trace. When a
 * `feathersBotConnector` is supplied, we compare the hash feathers-bot serves
 * for the latest saved config against the hash the controller says it's running
 * and refuse until they match, so teleop can't start against bounds the
 * controller isn't enforcing yet.
 *
 * `feathersBotConnector` is required rather than optional: pass an explicit
 * `undefined` where a context has none. Optional meant a new call site could
 * skip this fail-closed check by forgetting an argument.
 *
 * The `reason` is phrased to append after "Cannot start teleop: ".
 */
export async function checkEnabledCBFViolations(
  connector: ComplianceControlBotConnector | undefined,
  feathersBotConnector: FeathersBotConnector | undefined,
): Promise<CBFCheckResult> {
  if (!getFeatureFlagSync('complianceMode', false)) {
    return { ok: true };
  }

  if (!connector) {
    return failed(
      UNAVAILABLE_REASON,
      'compliance-control-bot connector not provided',
    );
  }

  let result: ListViolatedCBFsResponse;

  try {
    result = await connector.ListViolatedCBFs(
      {},
      LIST_VIOLATED_CBFS_TIMEOUT_MS,
    );
  } catch {
    // RPC timeout, controller down, transport error: fail closed with an
    // operator-facing reason rather than letting the rejection escape (which
    // the raw start path turns into a REQ-TIMEOUT blob and the routine step
    // into a reason-hiding Internal failure).
    return failed(
      UNAVAILABLE_REASON,
      'compliance-control-bot ListViolatedCBFs failed',
    );
  }

  if (!result.available) {
    return failed(
      NOT_READY_REASON,
      'compliance-control-bot reports available=false',
    );
  }

  if (result.violations.length > 0) {
    return failed(
      `in violation of ${formatViolations(result.violations)}`,
      `violated: ${result.violations.map((v) => v.name).join(', ')}`,
      result.violations,
    );
  }

  // Config-version handshake (see doc comment): refuse if the controller isn't
  // yet running the latest saved config. An empty latest hash means feathers
  // hasn't seeded one, so there is nothing to enforce against.
  if (feathersBotConnector) {
    let latestHash: string;

    try {
      latestHash = (
        await feathersBotConnector.GetComplianceConfig(
          {},
          GET_COMPLIANCE_CONFIG_TIMEOUT_MS,
        )
      ).configHash;
    } catch {
      // Can't reach feathers-bot for the latest saved config: fail closed.
      return failed(
        UNAVAILABLE_REASON,
        'feathers-bot GetComplianceConfig failed',
      );
    }

    if (latestHash && latestHash !== result.appliedConfigHash) {
      return failed(
        STALE_CONFIG_REASON,
        `running hash ${result.appliedConfigHash} != latest saved ${latestHash}`,
      );
    }
  }

  return { ok: true };
}
